/**
 * Which landlord bulk/status actions are allowed for a screening application,
 * keyed by the same {@link ApplicationStatus} used on the property applications list
 * (see deriveListStatus / resolveApplicationStatus).
 */
import {
	APPLICATION_STATUSES,
	type ApplicationStatus,
} from '#app/types/tenant-application'

export type LandlordGroupActions = {
	accept: boolean
	decline: boolean
	markCompleted: boolean
	withdraw: boolean
	abandon: boolean
}

/**
 * Landlord decisions are FINAL (DETAIL-V2-DECISION-RULES.md): once an
 * application reaches any of these statuses, no decision/status action may
 * move it again, its report may not be regenerated, and applicant-facing
 * request actions (guarantor / bank) are locked. UI hides the entry points;
 * the server guards (v2 detail actions, screening-regenerate) are the
 * enforcement. There is no path back to UNDECIDED (owner 2026-07-17):
 * a decision only ever becomes another decision — only platform staff via
 * existing superadmin tools may move a terminal application otherwise.
 */
export const TERMINAL_APPLICATION_STATUSES = [
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
] as const

/** True when a landlord decision has been recorded (raw OR derived status). */
export function isDecisionLocked(status: string): boolean {
	return (TERMINAL_APPLICATION_STATUSES as readonly string[]).includes(status)
}

/**
 * Map a raw status string (e.g. from DB) to {@link ApplicationStatus} for action gating.
 * Unknown values fall back to `incomplete` (same as list/detail derivation for non-terminal states).
 */
export function toApplicationStatusForLandlordActions(
	raw: string,
): ApplicationStatus {
	if ((APPLICATION_STATUSES as readonly string[]).includes(raw)) {
		return raw as ApplicationStatus
	}
	return 'incomplete'
}

const NO_ACTIONS: LandlordGroupActions = {
	accept: false,
	decline: false,
	markCompleted: false,
	withdraw: false,
	abandon: false,
}

/**
 * Allowed actions by household/list status — Decisions v3
 * (docs/landlord-dashboard-v2/DECISIONS-V3-PLAN.md): decisions are
 * per-member and CHANGEABLE, so the four decision entry points stay
 * available on DECIDED applications too — the DecisionDialog is the
 * console, and per-member legality (transition matrix, the per-member
 * report gate, the required note on changes) is enforced there and
 * by the server. This map only decides which BUTTONS render:
 * - incomplete: all four — a mixed household can hold members with
 *   finished reports, so Accept must be reachable; the dialog disables the
 *   Accept pill per member until THAT report is done (rule 5, per member).
 * - completed: all four; "mark completed" never re-offers.
 * - accepted/declined/withdrawn/abandoned: all four (decision changes live
 *   in the dialog). Requests/regenerate/add-member still lock via
 *   isDecisionLocked — rules 2/3 unchanged, and with no un-lock path.
 * - unknown statuses: NO actions (deny-by-default).
 */
export function getLandlordGroupActions(
	status: ApplicationStatus,
): LandlordGroupActions {
	if (isDecisionLocked(status)) {
		return {
			accept: true,
			decline: true,
			markCompleted: false,
			withdraw: true,
			abandon: true,
		}
	}
	switch (status) {
		case 'incomplete':
			return {
				accept: true,
				decline: true,
				markCompleted: true,
				withdraw: true,
				abandon: true,
			}
		case 'completed':
			return {
				accept: true,
				decline: true,
				markCompleted: false,
				withdraw: true,
				abandon: true,
			}
		default:
			return NO_ACTIONS
	}
}
